import { isDemoTenant } from '@lib';
import { Modal } from 'antd';
import { useCallback } from 'react';

const DEMO_TITLE = 'Not available on a trial account';

const DEMO_BODY =
  'This is part of the full Umoto account. Your trial covers everything else, and nothing you have entered will be lost when you upgrade.';

/**
 * Wraps actions that reach a paid third party — sending mail, taking a card payment,
 * buying credits, parsing an invoice, connecting accounting.
 *
 * The button stays visible on purpose: a demo user seeing what the product does and
 * being told how to unlock it is worth more than a button that was never there. The
 * server blocks these too, so this is about the message, not the enforcement.
 */
export const useDemoGuard = () => {
  const isDemo = isDemoTenant();

  const showDemoNotice = useCallback(() => {
    Modal.info({
      title: DEMO_TITLE,
      content: DEMO_BODY,
      okText: 'Got it',
    });
  }, []);

  /**
   * Returns a handler that shows the demo notice instead of running `action` when the
   * tenant is a demo account. Drop it straight into onClick.
   *
   * Always async, including around synchronous actions: callers that expect a
   * Promise-returning handler still typecheck, and a caller expecting void is happy with
   * a promise it ignores. Returning bare undefined instead would break anyone who chains
   * .then() on the result.
   */
  const guard = useCallback(
    <TArgs extends unknown[], TReturn>(action: (...args: TArgs) => TReturn) =>
      async (...args: TArgs): Promise<Awaited<TReturn> | undefined> => {
        if (isDemo) {
          showDemoNotice();
          return undefined;
        }

        return await action(...args);
      },
    [isDemo, showDemoNotice]
  );

  return { isDemo, guard, showDemoNotice };
};
